import * as React from "react"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"

const Stepper = React.forwardRef(
  ({ steps, currentStep, highestStep, onStepClick, className, ...props }, ref) => {
    // Use highestStep if provided, otherwise fall back to currentStep for backward compatibility
    const maxClickableStep = highestStep ?? currentStep

    return (
      <div ref={ref} className={cn("w-full", className)} {...props}>
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isCompleted = index < currentStep
            const isCurrent = index === currentStep
            const isUnlocked = index <= maxClickableStep
            const isClickable = onStepClick && isUnlocked

            return (
              <React.Fragment key={step.id || index}>
                {/* Step circle and label */}
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={() => isClickable && onStepClick(index)}
                    disabled={!isClickable}
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all",
                      isCompleted &&
                        "border-transparent bg-gradient-to-r from-blue-500 to-purple-500 text-white",
                      isCurrent &&
                        "border-blue-500 bg-blue-50 text-blue-600",
                      // Unlocked future steps (visited but not current)
                      !isCompleted &&
                        !isCurrent &&
                        isUnlocked &&
                        "border-blue-300 bg-blue-50/50 text-blue-500",
                      // Locked future steps (not yet visited)
                      !isCompleted &&
                        !isCurrent &&
                        !isUnlocked &&
                        "border-gray-300 bg-white text-gray-400",
                      isClickable && "cursor-pointer hover:scale-105",
                      !isClickable && "cursor-default"
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </button>
                  <span
                    className={cn(
                      "mt-2 text-xs font-medium text-center max-w-[80px]",
                      isCurrent && "text-blue-600",
                      isCompleted && "text-slate-700",
                      // Unlocked future steps
                      !isCompleted && !isCurrent && isUnlocked && "text-blue-500",
                      // Locked future steps
                      !isCompleted && !isCurrent && !isUnlocked && "text-gray-400"
                    )}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="flex-1 mx-2">
                    <div
                      className={cn(
                        "h-1 rounded-full transition-all",
                        isCompleted
                          ? "bg-gradient-to-r from-blue-500 to-purple-500"
                          : "bg-gray-200"
                      )}
                    />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    )
  }
)
Stepper.displayName = "Stepper"

// Vertical stepper variant
const VerticalStepper = React.forwardRef(
  ({ steps, currentStep, highestStep, onStepClick, className, ...props }, ref) => {
    // Use highestStep if provided, otherwise fall back to currentStep for backward compatibility
    const maxClickableStep = highestStep ?? currentStep

    return (
      <div ref={ref} className={cn("w-full", className)} {...props}>
        <div className="flex flex-col space-y-4">
          {steps.map((step, index) => {
            const isCompleted = index < currentStep
            const isCurrent = index === currentStep
            const isUnlocked = index <= maxClickableStep
            const isClickable = onStepClick && isUnlocked

            return (
              <div key={step.id || index} className="flex items-start">
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={() => isClickable && onStepClick(index)}
                    disabled={!isClickable}
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all",
                      isCompleted &&
                        "border-transparent bg-gradient-to-r from-blue-500 to-purple-500 text-white",
                      isCurrent &&
                        "border-blue-500 bg-blue-50 text-blue-600",
                      // Unlocked future steps
                      !isCompleted &&
                        !isCurrent &&
                        isUnlocked &&
                        "border-blue-300 bg-blue-50/50 text-blue-500",
                      // Locked future steps
                      !isCompleted &&
                        !isCurrent &&
                        !isUnlocked &&
                        "border-gray-300 bg-white text-gray-400",
                      isClickable && "cursor-pointer hover:scale-105",
                      !isClickable && "cursor-default"
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </button>
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        "w-0.5 h-8 mt-2 rounded-full",
                        isCompleted
                          ? "bg-gradient-to-b from-blue-500 to-purple-500"
                          : "bg-gray-200"
                      )}
                    />
                  )}
                </div>
                <div className="ml-4 flex-1">
                  <p
                    className={cn(
                      "text-sm font-medium",
                      isCurrent && "text-blue-600",
                      isCompleted && "text-slate-700",
                      // Unlocked future steps
                      !isCompleted && !isCurrent && isUnlocked && "text-blue-500",
                      // Locked future steps
                      !isCompleted && !isCurrent && !isUnlocked && "text-gray-400"
                    )}
                  >
                    {step.label}
                  </p>
                  {step.description && (
                    <p className="mt-1 text-xs text-gray-500">
                      {step.description}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
)
VerticalStepper.displayName = "VerticalStepper"

export { Stepper, VerticalStepper }
